import type { Session } from "@supabase/supabase-js";
import { create } from "zustand";

import { AuthMachineStatus } from "../../constants";
import supabase from "../../services/supabaseClient";
import { AuthEvent, authEventBus } from "../../utils/authEventBus";
import { sessionManager } from "../../utils/sessionManager";
import { usePermissionsV2Store } from "../permissionsV2Store";
import { reduce } from "./reducer";
import type { AuthMachineEvent, AuthMachineState } from "./types";
import { initialAuthMachineState } from "./types";

const SUBSCRIPTION_CHECK_STORAGE_KEY = "subscription_check_fresh_signin";

export const clearSubscriptionCheckStorage = (): void => {
  localStorage.removeItem(SUBSCRIPTION_CHECK_STORAGE_KEY);
};

/** True exactly once per browser sign-in "era" — mirrors the old useAuthStateChange helper. */
const getAndSetSubscriptionCheckStorage = (): boolean => {
  if (localStorage.getItem(SUBSCRIPTION_CHECK_STORAGE_KEY) === "true") {
    return true;
  }
  localStorage.setItem(SUBSCRIPTION_CHECK_STORAGE_KEY, "true");
  return false;
};

interface AuthMachineStore {
  state: AuthMachineState;
}

export const useAuthMachineStore = create<AuthMachineStore>(() => ({
  state: initialAuthMachineState,
}));

/** Public read hook for consumers outside the machine — the barrel exports this
 *  instead of the raw store so external code can't call `setState` on it. */
export const useAuthMachineState = (): AuthMachineState => useAuthMachineStore((s) => s.state);

export const currentGeneration = (): number => useAuthMachineStore.getState().state.generation;

// The machine is now the sole owner of the live Supabase session — module-private,
// no subscription (callers that need to react to session changes should read
// machine status/user instead; this is for one-off reads like building an Authorization
// header). Reset to null in initAuthMachine() so a fresh mount never sees a prior
// mount's session leak through this module singleton.
let authSession: Session | null = null;

/**
 * Records a session on the machine. Exported for two callers outside the
 * onAuthStateChange wiring in this file:
 *  - authOperations.notifyEmailConfirmed — EmailConfirmation's polling finds a
 *    session directly via supabase.auth.getSession(), which Supabase does not
 *    re-broadcast through onAuthStateChange.
 *  - AuthContext's refreshSession — a manual `supabase.auth.refreshSession()` call
 *    is likewise not re-broadcast as TOKEN_REFRESHED.
 */
export const setAuthSession = (session: Session | null): void => {
  authSession = session;
};

export const getAuthSession = (): Session | null => authSession;

// Wiring-scoped flags: set by operations before Supabase broadcasts the resulting
// event, consumed by the event translation below. Machine-internal — never exported.
let recoveryConfirmInFlight = false;
export const beginRecoveryConfirm = (): void => {
  recoveryConfirmInFlight = true;
};
export const cancelRecoveryConfirm = (): void => {
  recoveryConfirmInFlight = false;
};
const consumeRecoveryConfirm = (): boolean => {
  const value = recoveryConfirmInFlight;
  recoveryConfirmInFlight = false;
  return value;
};

let deliberateSignOutInFlight = false;
export const beginDeliberateSignOut = (): void => {
  deliberateSignOutInFlight = true;
};
export const cancelDeliberateSignOut = (): void => {
  deliberateSignOutInFlight = false;
};
const consumeDeliberateSignOut = (): boolean => {
  const value = deliberateSignOutInFlight;
  deliberateSignOutInFlight = false;
  return value;
};

// Count of machine-started updateUser-class operations in flight (updatePassword,
// writePasswordResetRequiredFlag). While > 0, incoming USER_UPDATED broadcasts are NOT
// translated to USER_REFRESHED: those operations' own results (PASSWORD_UPDATED /
// PASSWORD_RESET_FLAG_WRITE_SUCCEEDED / PASSWORD_UPDATE_FAILED /
// PASSWORD_RESET_FLAG_WRITE_FAILED) are the authoritative, generation-stamped channel
// for their outcome. USER_UPDATED is belt-and-suspenders only, never trusted for
// sequencing — without this gate, a slow/stale updateUser call (e.g. the flag write,
// held then released after the real password update already cleared the flag) can
// broadcast USER_UPDATED late and have its stale metadata unconditionally overwrite
// `state.user` via USER_REFRESHED, which carries no generation gate in the reducer.
let updateUserOpsInFlight = 0;
export const beginUpdateUserOp = (): void => {
  updateUserOpsInFlight += 1;
};
export const endUpdateUserOp = (): void => {
  updateUserOpsInFlight = Math.max(0, updateUserOpsInFlight - 1);
};
const isUpdateUserOpInFlight = (): boolean => updateUserOpsInFlight > 0;

// At most one password-reset-flag write may be in flight. Consulted by
// writePasswordResetRequiredFlag itself (test-and-set via beginPasswordResetFlagWrite),
// so every call site — the store's entry effect, the store's retry wiring below,
// future code — is deduplicated in one place instead of each needing its own
// race-aware guard. Distinct from updateUserOpsInFlight, which gates USER_UPDATED
// translation, not operation starts.
let passwordResetFlagWriteInFlight = false;
/** Returns false (and does nothing) if a flag write is already in flight. */
export const beginPasswordResetFlagWrite = (): boolean => {
  if (passwordResetFlagWriteInFlight) {
    return false;
  }
  passwordResetFlagWriteInFlight = true;
  return true;
};
export const endPasswordResetFlagWrite = (): void => {
  passwordResetFlagWriteInFlight = false;
};

/** Event types whose "no state change" outcome is normal (stale/idempotent), not suspicious. */
const QUIET_DROPS = new Set<AuthMachineEvent["type"]>([
  "PASSWORD_UPDATED",
  "PASSWORD_UPDATE_FAILED",
  "PASSWORD_RESET_FLAG_WRITE_SUCCEEDED",
  "PASSWORD_RESET_FLAG_WRITE_FAILED",
  "BOOT_COMPLETED",
  "SIGN_IN_MARKED_COMPLETE",
  "USER_REFRESHED",
  "SIGNED_IN",
  "EMAIL_CONFIRMED",
]);

export const dispatchAuthEvent = (event: AuthMachineEvent): void => {
  const prev = useAuthMachineStore.getState().state;
  const next = reduce(prev, event);
  if (next === prev) {
    if (import.meta.env.DEV && !QUIET_DROPS.has(event.type)) {
      console.warn(`[authMachine] dropped ${event.type} in state ${prev.status}`);
    }
    return;
  }
  useAuthMachineStore.setState({ state: next });
  runTransitionEffects(prev, next);
};

/** Side effects driven by committed transitions — the only place they may live. */
const runTransitionEffects = (prev: AuthMachineState, next: AuthMachineState): void => {
  const becameSignedOut =
    (next.status === AuthMachineStatus.UNAUTHENTICATED ||
      next.status === AuthMachineStatus.EXPIRED) &&
    prev.status !== AuthMachineStatus.UNAUTHENTICATED &&
    prev.status !== AuthMachineStatus.EXPIRED &&
    prev.status !== AuthMachineStatus.BOOTING;
  if (becameSignedOut) {
    setAuthSession(null);
    sessionManager.clearSessionId();
    clearSubscriptionCheckStorage();
    usePermissionsV2Store.getState().clearPermissions();
  }

  const leftResetPending =
    prev.status === AuthMachineStatus.PASSWORD_RESET_PENDING &&
    next.status !== AuthMachineStatus.PASSWORD_RESET_PENDING;
  if (leftResetPending) {
    // Orphaned-write case: if a flag write was in flight when the machine transitioned
    // away from passwordResetPending (e.g. SESSION_DIED/SIGNED_OUT racing a slow
    // writePasswordResetRequiredFlag write), passwordResetFlagWriteInFlight would
    // otherwise stay true until the orphan's own `finally` runs. That orphan's result
    // (PASSWORD_RESET_FLAG_WRITE_SUCCEEDED/PASSWORD_RESET_FLAG_WRITE_FAILED) is
    // generation-stale and gets dropped by the reducer, but a fresh recovery re-entry
    // into passwordResetPending before the orphan settles would have its own write
    // attempt swallowed by beginPasswordResetFlagWrite() still seeing the stale
    // in-flight flag. Reset it here so a future reset-entry can retry. The orphan's
    // own `finally` will still run later and clear it again — a harmless double-clear.
    endPasswordResetFlagWrite();
  }

  const enteredResetNeedingWrite =
    next.status === AuthMachineStatus.PASSWORD_RESET_PENDING &&
    prev.status !== AuthMachineStatus.PASSWORD_RESET_PENDING &&
    next.passwordResetFlagWritePending;
  // A user-context update (USER_REFRESHED, or a same-user SIGNED_IN re-confirmation)
  // committed while still in passwordResetPending with the write still pending —
  // e.g. a tab-refocus racing a still-in-flight write, or a fresh user reference
  // arriving after an earlier write attempt failed. Re-invoke the (self-gating) write
  // so a failed attempt isn't silently abandoned once nothing else would retry it.
  // Gated on an actual user-reference change (not merely staying in passwordResetPending)
  // so a failure alone — which leaves `user` untouched — cannot re-trigger itself into
  // a retry loop; only a subsequent context update can.
  const userContextUpdatedWhileWritePending =
    prev.status === AuthMachineStatus.PASSWORD_RESET_PENDING &&
    next.status === AuthMachineStatus.PASSWORD_RESET_PENDING &&
    next.passwordResetFlagWritePending &&
    prev.user !== next.user;
  if (enteredResetNeedingWrite || userContextUpdatedWhileWritePending) {
    // Deferred import breaks the store↔operations cycle at module-load time.
    void import("./operations").then(({ authOperations }) =>
      authOperations.writePasswordResetRequiredFlag()
    );
  }
};

/**
 * Resets machine state and wires all event sources. Called from AuthProvider's
 * mount effect (once per app tree, fresh per test render); returns cleanup.
 * Not module-init: the fake client in tests is swapped per test, and a
 * module-init subscription would bind to the first test's fake forever.
 */
export const initAuthMachine = (): (() => void) => {
  useAuthMachineStore.setState({ state: initialAuthMachineState });
  setAuthSession(null);
  recoveryConfirmInFlight = false;
  deliberateSignOutInFlight = false;
  updateUserOpsInFlight = 0;
  passwordResetFlagWriteInFlight = false;

  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange(async (event, session) => {
    switch (event) {
      case "INITIAL_SESSION": {
        if (!session) {
          dispatchAuthEvent({ type: "BOOT_COMPLETED", user: null });
          return;
        }
        const now = Math.floor(Date.now() / 1000);
        if (session.expires_at && session.expires_at <= now) {
          const { data, error } = await supabase.auth.refreshSession();
          if (error || !data.session) {
            console.error("[authMachine] Boot-session refresh failed; routing to expired:", error);
            dispatchAuthEvent({ type: "BOOT_SESSION_EXPIRED" });
            return;
          }
          session = data.session;
        }
        setAuthSession(session);
        dispatchAuthEvent({ type: "BOOT_COMPLETED", user: session.user });
        return;
      }

      // auth-js sends a recovery verifyOtp to PASSWORD_RECOVERY and every other sign-in to
      // SIGNED_IN; both mean "a session just arrived" and take the same path from here,
      // differing only in how the recovery intent is established.
      case "PASSWORD_RECOVERY":
      case "SIGNED_IN": {
        if (!session) return;
        setAuthSession(session);
        // Consumed unconditionally, even when the event already implies recovery: the flag
        // lives for the tab, so leaving it set would misclassify the next ordinary sign-in.
        const isRecoveryConfirm = consumeRecoveryConfirm() || event === "PASSWORD_RECOVERY";
        if (isRecoveryConfirm) {
          dispatchAuthEvent({ type: "RECOVERY_SIGNED_IN", user: session.user });
          return;
        }
        const machineUser = useAuthMachineStore.getState().state.user;
        const isSameUser = machineUser?.id === session.user.id;
        // Fresh-sign-in bookkeeping mirrors the legacy handler exactly: gated only
        // on "not a recovery confirm" (already consumed above) and not a same-user
        // re-confirmation. Reset-required (invite/reset) users still get session-id
        // bookkeeping here — the legacy handler never special-cased them.
        let freshSignIn = false;
        if (!isSameUser) {
          freshSignIn = !getAndSetSubscriptionCheckStorage();
          await sessionManager.createAndSetSessionId();
        }
        dispatchAuthEvent({ type: "SIGNED_IN", user: session.user, freshSignIn });
        return;
      }

      case "TOKEN_REFRESHED":
        if (session) {
          setAuthSession(session);
        }
        return;

      case "USER_UPDATED":
        // Belt-and-suspenders only — never relied on for sequencing. While a
        // machine-started updateUser-class op (updatePassword/writePasswordResetRequiredFlag)
        // is in flight, its own generation-stamped result event is the authoritative
        // channel; drop this broadcast so a stale/late one can't clobber state.user.
        if (session && !isUpdateUserOpInFlight()) {
          dispatchAuthEvent({ type: "USER_REFRESHED", user: session.user });
        }
        return;

      case "SIGNED_OUT":
        dispatchAuthEvent({ type: "SIGNED_OUT", deliberate: consumeDeliberateSignOut() });
        return;
    }
  });

  const unsubscribeBus = authEventBus.subscribe((event) => {
    if (event === AuthEvent.LOGOUT) {
      console.warn("[authMachine] 401-exhaustion LOGOUT — marking session expired");
      dispatchAuthEvent({ type: "SESSION_DIED" });
    }
  });

  return () => {
    subscription.unsubscribe();
    unsubscribeBus();
  };
};
